import type { SelectProps } from '@ztechapps/webui.input'
import { Select } from '@ztechapps/webui.input'
import { useSpreadsheetCellSelectAdapter } from './useSpreadsheetCellSelectAdapter'

/**
 * Adapter wrapping `<Select>` for spreadsheet cells. Shared logic (autoFocus override, menu-state
 * forwarding, onChange→onCommit chaining) lives in `useSpreadsheetCellSelectAdapter`.
 *
 * - `onCommit` fires after `onChange`. The cell consumer exits edit mode here.
 * - `defaultMenuIsOpen` / `defaultInputValue` pass through to react-select to seed the dropdown
 *   open with an initial filter character.
 * - Menus render inside the cell (`SPREADSHEET_CELL_MENU_PROPS`) so the open menu scrolls with
 *   the grid instead of floating over the page body.
 */
export type SpreadsheetCellSelectMode = {
  onMenuStateChange?: (isOpen: boolean) => void
  onCommit?: () => void
  defaultMenuIsOpen?: boolean
  defaultInputValue?: string
}

export const SPREADSHEET_CELL_MENU_PROPS = {
  menuPlacement: 'auto' as const,
  menuPosition: 'absolute' as const,
  minMenuHeight: 240,
  maxMenuHeight: 240,
  menuPortalTarget: null,
}

export type SpreadsheetCellSelectProps = SelectProps & {
  onCommit?: () => void
  onMenuStateChange?: (isOpen: boolean) => void
}

export const SpreadsheetCellSelect = ({
  onCommit,
  onMenuStateChange,
  onChange,
  onInputChange,
  onMenuOpen,
  onMenuClose,
  ...rest
}: SpreadsheetCellSelectProps) => {
  const { wrapperProps, selectProps } = useSpreadsheetCellSelectAdapter({
    onCommit,
    onMenuStateChange,
    onChange,
    onInputChange,
    onMenuOpen,
    onMenuClose,
    defaultMenuIsOpen: rest.defaultMenuIsOpen,
    defaultInputValue: rest.defaultInputValue,
    options: rest.options,
  })

  return (
    <div {...wrapperProps}>
      <Select {...rest} {...selectProps} />
    </div>
  )
}
